import Phaser from 'phaser';
import { onAuthChange, signOut } from '../../store/auth.js';
import {
  createProfile,
  getProfile,
  onProfileChange,
  USERNAME_REGEX,
} from '../../store/profile.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { Button } from '../view/Button.js';
import { TextInputOverlay } from '../view/TextInputOverlay.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

/**
 * First-login username capture. Reached automatically by HomeScene
 * the first time a signed-in user lands without a row in
 * public.profiles — typically right after clicking the email
 * confirmation link.
 *
 * Validation mirrors what the DB enforces (regex + case-insensitive
 * uniqueness via 0005_profiles.sql). Client checks the regex before
 * the round-trip; the server is the source of truth for uniqueness
 * (only the DB knows whether someone else already has this name).
 *
 * Escape hatches: a sign-out button at the bottom — without it, a
 * signed-in user with no profile would be trapped here forever
 * (every visit to HomeScene auto-routes back) with no way to bail.
 */
export class UsernameSetupScene extends Phaser.Scene {
  static readonly KEY = 'UsernameSetup';

  private username = '';
  private submitting = false;

  private usernameInput: TextInputOverlay | undefined = undefined;
  private submitButton: Button | undefined = undefined;
  private signOutButton: Button | undefined = undefined;
  private statusText!: Phaser.GameObjects.Text;
  private unsubscribeProfile: (() => void) | undefined = undefined;
  private unsubscribeAuth: (() => void) | undefined = undefined;
  // Deep-link return target. When the user lands on /game/:id, gets
  // routed through Auth, then arrives here without a profile,
  // setting their username bounces them straight to that game's
  // lobby instead of Home.
  private returnToGameId: string | undefined = undefined;

  constructor() {
    super(UsernameSetupScene.KEY);
  }

  preload(): void {
    this.load.image('usernameSetupLogo', '/assets/logo2.png');
  }

  init(data?: { returnToGameId?: string }): void {
    this.username = '';
    this.submitting = false;
    this.usernameInput = undefined;
    this.submitButton = undefined;
    this.signOutButton = undefined;
    this.unsubscribeProfile = undefined;
    this.unsubscribeAuth = undefined;
    this.returnToGameId = data?.returnToGameId;
  }

  create(): void {
    this.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        COLORS.bgDeepInt
      )
      .setOrigin(0.5);

    this.drawLogo();
    this.drawHeader();
    this.drawForm();
    this.drawSubmit();
    this.drawStatus();
    this.drawSignOut();

    // If profile state flips to 'exists' while we're here (e.g., the
    // create-profile call we kicked off returned and the cache got
    // populated), bounce. If we came in via a /game/:id deep link,
    // return there; else Home.
    this.unsubscribeProfile = onProfileChange((state) => {
      if (state.kind === 'exists') {
        if (this.returnToGameId) {
          this.scene.start('Join', { gameId: this.returnToGameId });
        } else {
          this.scene.start('Home');
        }
      }
    });
    // Sign-out is the escape hatch from this scene. Bounces to
    // Home regardless of returnToGameId — there's no game to
    // return to once you've signed out.
    this.unsubscribeAuth = onAuthChange((auth) => {
      if (!auth.user) {
        this.scene.start('Home');
      }
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.usernameInput?.destroy();
      this.submitButton?.destroy();
      this.signOutButton?.destroy();
      this.unsubscribeProfile?.();
      this.unsubscribeAuth?.();
    });
  }

  // --- Layout ---

  private drawLogo(): void {
    const logo = this.add.image(DESIGN_WIDTH / 2, 0, 'usernameSetupLogo');
    const targetWidth = 540;
    const scale = Math.min(1, targetWidth / logo.width);
    logo.setScale(scale);
    const logoH = logo.height * scale;
    logo.setY(70 + logoH / 2);
  }

  private drawHeader(): void {
    this.add
      .text(DESIGN_WIDTH / 2, 320, 'Pick a username', {
        fontFamily: FONT_HEADER,
        fontSize: '38px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    this.add
      .text(
        DESIGN_WIDTH / 2,
        390,
        'Shown to other players in your games.',
        {
          fontFamily: FONT_MONO,
          fontSize: '16px',
          color: COLORS.textMuted,
        }
      )
      .setLetterSpacing(2)
      .setOrigin(0.5);

    // Constraint hint sits below the subtitle. Keeps the rules
    // visible during typing without an extra modal.
    this.add
      .text(
        DESIGN_WIDTH / 2,
        430,
        '3–20 chars: letters, numbers, _ - or .',
        {
          fontFamily: FONT_MONO,
          fontSize: '13px',
          color: COLORS.textSecondary,
        }
      )
      .setLetterSpacing(2)
      .setOrigin(0.5);
  }

  private drawForm(): void {
    const cx = DESIGN_WIDTH / 2;
    const fieldWidth = 520;
    const fieldHeight = 64;

    this.add
      .text(cx - fieldWidth / 2, 528, 'USERNAME', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.textSecondary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);

    this.usernameInput = new TextInputOverlay({
      scene: this,
      x: cx,
      y: 572,
      width: fieldWidth,
      height: fieldHeight,
      type: 'text',
      placeholder: 'Reefclaw',
      autocomplete: 'username',
      maxLength: 20,
      fontSizeDesign: 26,
      onChange: (v) => {
        this.username = v;
        // Clear any stale error message as the user retypes.
        if (this.statusText?.text) this.statusText.setText('');
      },
      onSubmit: () => {
        void this.handleSubmit();
      },
    });
  }

  private drawSubmit(): void {
    this.submitButton = new Button(this, {
      x: DESIGN_WIDTH / 2,
      y: 690,
      width: 360,
      height: 80,
      text: 'Save username',
      style: 'primary',
      onTap: () => {
        void this.handleSubmit();
      },
    });
  }

  private drawStatus(): void {
    this.statusText = this.add
      .text(DESIGN_WIDTH / 2, 780, '', {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color: COLORS.accentRedHex,
        fontStyle: '700',
        align: 'center',
        wordWrap: { width: 560 },
      })
      .setLetterSpacing(1)
      .setOrigin(0.5);
  }

  private drawSignOut(): void {
    // Default-style button — escape hatch from the username gate.
    // Lower hierarchy than the primary "Save username" CTA, but
    // tappable enough to spot on the dark backdrop.
    this.signOutButton = new Button(this, {
      x: DESIGN_WIDTH / 2,
      y: 1140,
      width: 240,
      height: 64,
      text: 'Sign out',
      onTap: () => {
        if (this.submitting) return;
        void signOut();
      },
    });
  }

  // --- Submission ---

  private async handleSubmit(): Promise<void> {
    if (this.submitting) return;

    const trimmed = this.username.trim();
    if (!trimmed) {
      this.setError('Enter a username.');
      this.usernameInput?.focus();
      return;
    }
    if (!USERNAME_REGEX.test(trimmed)) {
      this.setError(
        'Username must be 3–20 characters: letters, numbers, _ - or .'
      );
      this.usernameInput?.focus();
      return;
    }

    this.setSubmitting(true);
    const result = await createProfile(trimmed);
    if (!result.ok) {
      this.setError(result.error);
      return;
    }

    // Success — profile listener (above) will route as soon as the
    // post-insert refetch flips state to 'exists'. Defensive
    // fallback in case the listener somehow misses the transition.
    const profile = getProfile();
    if (profile.kind === 'exists') {
      if (this.returnToGameId) {
        this.scene.start('Join', { gameId: this.returnToGameId });
      } else {
        this.scene.start('Home');
      }
    }
  }

  private setSubmitting(submitting: boolean): void {
    this.submitting = submitting;
    this.usernameInput?.setDisabled(submitting);
    if (submitting) {
      this.statusText.setColor(COLORS.accentAmberHex);
      this.statusText.setText('Saving…');
    }
  }

  private setError(msg: string): void {
    this.submitting = false;
    this.usernameInput?.setDisabled(false);
    this.statusText.setColor(COLORS.accentRedHex);
    this.statusText.setText(msg);
  }
}
